/* @flow */
import * as React from 'react';

import { Container } from 'flux/utils';
import Button from 'components/ButtonDeprecated';
import ChatSettingsActions from 'settings/actions/ChatSettingsActions';
import ChatSettingsStore from 'settings/stores/ChatSettingsStore';
import CheckboxList from 'components/CheckboxList';
import CurrentUser from 'auth/CurrentUser';
import DateConstants from 'constants/DateConstants';
import Fieldset from 'components/Fieldset';
import Label from 'components/Label';
import Legend from 'components/Legend';
import Link from 'components/Link';
import OfficeHours from 'entities/OfficeHours';
import OfficeHoursDay from 'entities/OfficeHoursDay';
import Text from 'components/Text';
import TimePickerInput from 'components/TimePickerInput';
import ToastActions from 'actions/ToastActions';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';

import cx from 'classnames';

import './styles/OfficeHoursPicker.scss';

const OFFICE_HOUR_DAYS = new Map();
const OFFICE_HOUR_DAYS_COMPACT = new Map();
[0, 1, 2, 3, 4, 5, 6].forEach(index => {
  OFFICE_HOUR_DAYS.set(index, DateConstants.WEEKDAYS[index]);
  OFFICE_HOUR_DAYS_COMPACT.set(index, DateConstants.WEEKDAYS_COMPACT[index]);
});

type OfficeHourTimes = {
  startTime: ?Date,
  endTime: ?Date,
};

type Props = {
  showSubmitButton: ?boolean,
  showToggleButton: ?boolean,
  showToastMessages: ?boolean,
  showIntroText: ?boolean,
  twoWayMessagingEnabled: ?boolean,
  onSubmit: ?Function,
  compact: ?boolean,
};

type State = {
  officeHours: ?OfficeHours,
  startTime: ?Date,
  endTime: ?Date,
  availabilityChanges: OfficeHourTimes,
};

class OfficeHoursPicker extends React.Component<Props, State> {
  state: State = {
    officeHours: null,
    startTime: null,
    endTime: null,
    availabilityChanges: {
      startTime: null,
      endTime: null,
    },
  };

  static getStores(): Array<Object> {
    return [ChatSettingsStore, CurrentUser];
  }

  static calculateState(prevState: State) {
    const officeHours = ChatSettingsStore.getOfficeHours();
    let startTime;
    let endTime;
    if (officeHours) {
      const availability = officeHours.getAvailability();
      startTime = officeHours.hasAvailability()
        ? OfficeHoursPicker.getDateObject(availability[0].getStartTime())
        : null;
      endTime = officeHours.hasAvailability()
        ? OfficeHoursPicker.getDateObject(availability[0].getEndTime())
        : null;
    }

    return {
      officeHours,
      startTime,
      endTime,
      availabilityChanges: (prevState && prevState.availabilityChanges) || {
        startTime: null,
        endTime: null,
      },
    };
  }

  static getDateObject(seconds: number): Date {
    // start and end time from API are stored as seconds
    // DateTimePicker expects Date object
    // this converts API seconds to Date object for the DateTimePicker
    return new Date(2015, 0, 0, 0, 0, seconds);
  }

  getSecondsFromTime(time: ?Date): number {
    if (!time) {
      return 0;
    }
    // this converts Date object from DateTimePicker into seconds for API

    const SECONDS_IN_HOUR = 60 * 60;
    const SECONDS_IN_MIN = 60;

    const date = new Date(time);
    return date.getHours() * SECONDS_IN_HOUR + date.getMinutes() * SECONDS_IN_MIN;
  }

  commitAvailabilityChanges(newTimes: OfficeHourTimes) {
    const { showToastMessages } = this.props;
    const days = this.refs.days.getValue();
    const newStartTime = newTimes.startTime
      ? this.getSecondsFromTime(newTimes.startTime)
      : this.getSecondsFromTime(this.state.startTime);
    const newEndTime = newTimes.endTime
      ? this.getSecondsFromTime(newTimes.endTime)
      : this.getSecondsFromTime(this.state.endTime);
    const available = days.map(day => OfficeHoursDay.create(day, newStartTime, newEndTime));

    ChatSettingsActions.setOfficeHours(available)
      .then(() => showToastMessages && ToastActions.showSuccessToast('Office hours updated'))
      .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});

    if (this.props.showSubmitButton && this.props.onSubmit) {
      this.props.onSubmit();
    }
  }

  handleAvailabilityChange(newTimes: OfficeHourTimes) {
    if (this.props.showSubmitButton) {
      if (newTimes.startTime) {
        this.setState({ startTime: newTimes.startTime });
      }
      if (newTimes.endTime) {
        this.setState({ endTime: newTimes.endTime });
      }
      this.setState({
        availabilityChanges: Object.assign(this.state.availabilityChanges, newTimes),
      });
    } else {
      this.commitAvailabilityChanges(newTimes);
    }
  }

  handleEndChange(newEndTime: ?Date) {
    if (newEndTime) {
      this.handleAvailabilityChange({ startTime: null, endTime: newEndTime });
    }
  }

  handleStartChange(newStartTime: ?Date) {
    if (newStartTime) {
      this.handleAvailabilityChange({ startTime: newStartTime, endTime: null });
    }
  }

  toggleOfficeHours() {
    const { showToastMessages } = this.props;
    const { officeHours } = this.state;

    if (officeHours && officeHours.hasAvailability()) {
      ChatSettingsActions.disableOfficeHours()
        .then(
          () =>
            showToastMessages && ToastActions.showSuccessToast('Office hours have been canceled')
        )
        .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});
    } else {
      ChatSettingsActions.enableOfficeHours()
        .then(
          () => showToastMessages && ToastActions.showSuccessToast('Office hours have been set')
        )
        .catch(showToastMessages ? ToastActions.showDefaultErrorToast : () => {});
    }
  }

  renderOptions() {
    const officeHours = this.state.officeHours;
    if (!officeHours) {
      return null;
    }
    const availability = officeHours.getAvailability();

    const times = (
      <div>
        <TimePickerInput
          ref="startTime"
          value={this.state.startTime}
          max={this.state.endTime}
          onChange={newStartTime => this.handleStartChange(newStartTime)}
        />
        <Text color="graphite" size="large">
          {' - '}
        </Text>
        <TimePickerInput
          ref="endTime"
          value={this.state.endTime}
          min={this.state.startTime}
          onChange={newEndTime => this.handleEndChange(newEndTime)}
        />
      </div>
    );

    const days = (
      <CheckboxList
        ref="days"
        className="OfficeHoursPicker-officeHoursDays"
        options={this.props.compact ? OFFICE_HOUR_DAYS_COMPACT : OFFICE_HOUR_DAYS}
        defaultChecked={availability.map(day => day.getDay())}
        onChange={newTimes => this.handleAvailabilityChange(newTimes)}
        direction={this.props.compact ? 'horizontal' : 'vertical'}
      />
    );

    return (
      <div className={cx({ 'OfficeHoursPicker-compactDateTime': this.props.compact })}>
        <div className="OfficeHoursPicker-timePicker">
          <Label>Time</Label>
          {times}
        </div>
        <div className="OfficeHoursPicker-datePicker">
          <Label>Day(s)</Label>
          {days}
        </div>
      </div>
    );
  }

  render() {
    let options;
    let toggleButton;

    const { twoWayMessagingEnabled } = this.props;
    const { officeHours } = this.state;
    const officeHoursAreOn = twoWayMessagingEnabled && officeHours && officeHours.hasAvailability();
    if (officeHoursAreOn && officeHours) {
      options = this.renderOptions();

      toggleButton = (
        <Button
          color="graphite"
          level="text"
          size="xsmall"
          key="enabled"
          onClick={() => this.toggleOfficeHours()}>
          Turn off office hours
        </Button>
      );
    } else {
      toggleButton = (
        <Button
          key="disabled"
          level="secondary"
          size="xsmall"
          disabled={!twoWayMessagingEnabled}
          onClick={() => this.toggleOfficeHours()}>
          Turn on office hours
        </Button>
      );
    }

    const submitButton = (
      <Button
        className="OfficeHoursPicker-submit"
        level="primary"
        size="xsmall"
        onClick={() => this.commitAvailabilityChanges(this.state.availabilityChanges)}
        eventElementName="OfficeHoursPicker-submit">
        Set office hours
      </Button>
    );

    const introText = (
      <div>
        Office hours
        <p>Let people know the best times to reach you.</p>
      </div>
    );

    return (
      <Fieldset>
        <Legend>
          {this.props.showIntroText && introText}
          {!twoWayMessagingEnabled && (
            <p>
              To enable Office hours, set Message options to “Allow replies to your messages” at the
              bottom of your{' '}
              <Link to={SETTINGS_ROUTES.NOTIFICATIONS}>Notification preferences</Link>
            </p>
          )}
        </Legend>
        {options}
        {this.props.showToggleButton && toggleButton}
        {this.props.showSubmitButton && officeHoursAreOn && submitButton}
      </Fieldset>
    );
  }
}

export default Container.create(OfficeHoursPicker);
